// CosmicLockPanel — Iter160v (Randy pt-cosmic-lock, Feb 2026).
//
// A single boolean toggle at the TOP of the Super Admin's Platform Config
// tab that controls what /api/marketing/featured-silo returns for the
// landing page's "See a live silo" button.
//
//   ON  (default) → /marketing/featured-silo always returns the silo
//                    with slug="cosmic". No filters, no ranking, no
//                    hidden/suspended checks. Just a slug lookup.
//   OFF          → the pre-existing organic-rotation aggregation runs
//                    (each creator's top-sorted silo, tie-break by
//                    most-recently-updated). See server.py's
//                    featured_demo_silo() docstring for the full ranking
//                    spec.
//
// Backed by two super-admin-only endpoints:
//   GET   /api/admin/platform-config/cosmic-lock → { enabled: bool }
//   PATCH /api/admin/platform-config/cosmic-lock  { enabled: bool }

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, LockOpen, ArrowsClockwise } from "@phosphor-icons/react";

export default function CosmicLockPanel() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/platform-config/cosmic-lock")
      .then((r) => { if (alive) setEnabled(!!r.data.enabled); })
      .catch(() => { if (alive) setEnabled(true); }); // fail-safe: assume locked
    return () => { alive = false; };
  }, []);

  const flip = async () => {
    if (enabled == null || saving) return;
    const next = !enabled;
    setSaving(true);
    try {
      await api.patch("/admin/platform-config/cosmic-lock", { enabled: next });
      setEnabled(next);
      toast.success(next
        ? "Cosmic Lock ON — landing page demo is locked to Cosmic."
        : "Cosmic Lock OFF — landing page demo rotates organically across active creators.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    } finally {
      setSaving(false);
    }
  };

  const isLoading = enabled == null;

  return (
    <section
      className="border hairline rounded-lg bg-white p-5"
      data-testid="admin-cosmic-lock-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {enabled ? (
              <Lock size={16} weight="duotone" className="text-[#2F6B3A] shrink-0" />
            ) : (
              <LockOpen size={16} weight="duotone" className="text-[#8A6A3A] shrink-0" />
            )}
            <h3 className="text-[15px] font-medium text-[#1C1B1A]">Cosmic Lock</h3>
            {isLoading && (
              <ArrowsClockwise size={11} className="text-[#6B6A68] animate-spin ml-1" />
            )}
          </div>
          <p className="text-[12.5px] text-[#4A5D4E] leading-relaxed mt-1.5 max-w-[62ch]">
            When <strong className="text-[#2F6B3A]">ON</strong> (default), the landing page&apos;s <em>&ldquo;See a live silo&rdquo;</em> button always demos your Cosmic silo. When <strong className="text-[#8A6A3A]">OFF</strong>, the demo rotates organically across active creators (whichever creator&apos;s top-of-Dashboard silo was updated most recently).
          </p>
          {!isLoading && (
            <p className="text-[11.5px] text-[#6B6A68] italic mt-2">
              Current state: <strong className={enabled ? "text-[#2F6B3A]" : "text-[#8A6A3A]"}>{enabled ? "LOCKED to Cosmic" : "Organic rotation"}</strong>
            </p>
          )}
        </div>
        <button
          onClick={flip}
          disabled={isLoading || saving}
          data-testid="admin-cosmic-lock-toggle"
          className={`shrink-0 relative inline-flex h-7 w-14 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            enabled ? "bg-[#2F6B3A]" : "bg-[#D8D6D2]"
          }`}
          title={enabled ? "Lock is ON — click to release" : "Lock is OFF — click to engage"}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
              enabled ? "translate-x-8" : "translate-x-1"
            } mt-1`}
          />
        </button>
      </div>
    </section>
  );
}
